<template>
  <label
    class="zm-switch"
    :class="{ 'is-disabled': switchDisabled, 'is-checked': checked }"
  >
    <input
      class="zm-switch__input"
      type="checkbox"
      @click="debouncedOnInputChange"
      ref="input"
      :id="id"
      :name="name"
      :true-value="activeValue"
      :false-value="inactiveValue"
      :disabled="switchDisabled"
      @keydown.enter="switchValue"
      autocomplete="off"
      role="button"
      data-toggle="button"
      :aria-pressed="checked.toString()"
      :aria-describedby="labelBy"
      :aria-label="labelText"
    >
    <span
      :class="['zm-switch__label', 'zm-switch__label--left', !checked ? 'is-active' : '']"
      v-if="inactiveIconClass || inactiveText">
      <i :class="[inactiveIconClass]" v-if="inactiveIconClass"></i>
      <span v-if="!inactiveIconClass && inactiveText" :aria-hidden="checked">{{ inactiveText }}</span>
    </span>
    <span class="zm-switch__core" ref="core">
    </span>
    <span v-text="labelText" v-show="false"></span>
    <span
      :class="['zm-switch__label', 'zm-switch__label--right', checked ? 'is-active' : '']"
      v-if="activeIconClass || activeText">
      <i :class="[activeIconClass]" v-if="activeIconClass"></i>
      <span v-if="!activeIconClass && activeText" :aria-hidden="!checked">{{ activeText }}</span>
    </span>
  </label>
</template>
<script>
  import Focus from '../../mixins/focus';
  import debounce from 'throttle-debounce/debounce';

  export default {
    name: 'ZmSwitch',
    mixins: [Focus('input')],
    inject: {
      ZmForm: {
        default: ''
      }
    },
    props: {
      value: {
        type: [Boolean, String, Number],
        default: false
      },
      disabled: {
        type: Boolean,
        default: false
      },
      activeIconClass: {
        type: String,
        default: ''
      },
      inactiveIconClass: {
        type: String,
        default: ''
      },
      activeText: String,
      inactiveText: String,
      activeColor: {
        type: String,
        default: ''
      },
      inactiveColor: {
        type: String,
        default: ''
      },
      activeValue: {
        type: [Boolean, String, Number],
        default: true
      },
      inactiveValue: {
        type: [Boolean, String, Number],
        default: false
      },
      name: {
        type: String,
        default: ''
      },
      id: String,
      labelText: String,
      labelBy: String
    },
    data() {
      return {};
    },
    created() {
      if (!~[this.activeValue, this.inactiveValue].indexOf(this.value)) {
        this.$emit('input', this.inactiveValue);
      }
      this.debouncedOnInputChange = debounce(300, () => {
        this.handleChange();
      });
    },
    computed: {
      checked() {
        return this.value === this.activeValue;
      },
      switchDisabled() {
        return this.disabled || (this.ZmForm || {}).disabled;
      }
    },
    watch: {
      checked() {
        this.$refs.input.checked = this.checked;
        if (this.activeColor || this.inactiveColor) {
          this.setBackgroundColor();
        }
      }
    },
    methods: {
      handleChange() {
        this.$emit('input', !this.checked ? this.activeValue : this.inactiveValue);
        this.$emit('change', !this.checked ? this.activeValue : this.inactiveValue);
        this.$nextTick(() => {
          // set input's checked property
          // in case parent refuses to change component's value
          this.$refs.input.checked = this.checked;
        });
      },
      setBackgroundColor() {
        let newColor = this.checked ? this.activeColor : this.inactiveColor;
        this.$refs.core.style.borderColor = newColor;
        this.$refs.core.style.backgroundColor = newColor;
      },
      switchValue() {
        !this.switchDisabled && this.handleChange();
      }
    },
    mounted() {
      /* istanbul ignore if */
      if (this.activeColor || this.inactiveColor) {
        this.setBackgroundColor();
      }
      this.$refs.input.checked = this.checked;
    }
  };
</script>

<style lang="scss">
  @import "../../scss/mixins/mixins";
  @import "../../scss/common/var";

  @include b(switch) {
    display: inline-flex;
    align-items: center;
    position: relative;
    font-size: $--switch-font-size;
    line-height: $--switch-height;
    height: $--switch-height;
    vertical-align: middle;
    @include when(disabled) {
      & .zm-switch__core,
      & .zm-switch__label {
        cursor: not-allowed;
      }
    }

    @include e(input) {
      position: absolute;
      width: 0;
      height: 0;
      opacity: 0;
      margin: 0;

      &:checked ~ .zm-switch__core {
        border-color: $--switch-on-color;
        background-color: $--switch-on-color;
        &::after {
          left: 17px;
          border: $--switch-button-size/2 solid $--color-white;
          width: 0;
          height: 0;
        }
      }
    }

    @include e(core) {
      margin: 0;
      display: inline-block;
      position: relative;
      width: $--switch-width;
      height: $--switch-height;
      border: 1px solid transparent;
      outline: none;
      border-radius: $--switch-core-border-radius;
      box-sizing: border-box;
      background: $--switch-off-color;
      cursor: pointer;
      transition: border-color .3s, background-color .3s;
      vertical-align: middle;

      &:after {
        content: "";
        position: absolute;
        top: 1px;
        left: 1px;
        border-radius: $--border-radius-circle;
        transition: left .3s;
        width: $--switch-button-size;
        height: $--switch-button-size;
        background-color: $--color-white;
      }
    }

    @include when(disabled) {
      opacity: 0.35;
    }

    @include m(wide) {
      .zm-switch__label {
        &.zm-switch__label--left {
          span {
            left: 10px;
          }
        }
        &.zm-switch__label--right {
          span {
            right: 10px;
          }
        }
      }
    }

    & .label-fade-enter,
    & .label-fade-leave-active {
      opacity: 0;
    }
  }

  /* 只有通过键盘操作聚焦的时候才会显示边框*/
  .is-keyboard-event .zm-switch__input:focus ~ .zm-switch__core {
    box-shadow: 0 0 0 2px #fff, 0 0 0 4px $--color-primary;
  }
  @media (forced-colors: active) {
    .is-keyboard-event .zm-switch__input:focus ~ .zm-switch__core {
      outline: dotted thin !important;
      outline-offset: 4px;
    }
  }
</style>
